import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

/**
 * TextareaField - Textarea with label, error, and helper text
 * @param {Object} props
 * @param {string} props.label - Label text
 * @param {string} props.error - Error message
 * @param {string} props.helperText - Helper text shown below textarea
 * @param {boolean} props.required - Show required asterisk
 * @param {string} props.className - Container className
 * @param {string} props.labelClassName - Label className
 * @param {string} props.textareaClassName - Textarea className
 * @param {number} props.rows - Number of rows (default: 4)
 * @param {number} props.maxLength - Maximum character length
 * @param {boolean} props.showCount - Show character count
 */
export const TextareaField = ({
  label,
  error,
  helperText,
  required,
  className,
  labelClassName,
  textareaClassName,
  rows = 4,
  maxLength,
  showCount = false,
  value = "",
  ...props
}) => {
  const currentLength = String(value || "").length;

  return (
    <div className={cn("space-y-2", className)}>
      <div className="flex items-center justify-between">
        {label && (
          <Label className={labelClassName}>
            {label}
            {required && <span className="text-destructive ml-1">*</span>}
          </Label>
        )}
        {showCount && maxLength && (
          <span className="text-xs text-muted-foreground">
            {currentLength} / {maxLength}
          </span>
        )}
      </div>

      <Textarea
        rows={rows}
        maxLength={maxLength}
        value={value}
        className={cn(error && "border-destructive", textareaClassName)}
        {...props}
      />

      {error && <p className="text-sm text-destructive">{error}</p>}
      {!error && helperText && (
        <p className="text-sm text-muted-foreground">{helperText}</p>
      )}
    </div>
  );
};
